import React from 'react'
import { Card, Dropdown } from 'react-bootstrap'
import { Deal } from '../models/shared/Deal'
import { useText } from '../context/TextContext'
import { useSortable } from '@dnd-kit/sortable'
import { ThreeDotsToggle } from './ThreeDotsToggle'
import { Link } from 'react-router-dom'
import { COLLECTIONS, PATHS } from '../utils/shared/constants'
import { CardLayout } from '../models/shared/CardLayout'
import Tags from './Tags'
import { renderDealContent } from '../utils/presentation'
import { isDealSeparator } from '../utils/shared/deal'
import { AdvancedDropdownItem } from './AdvancedDropdownItem'
import DealDueDate from './DealDueDate'
import { Offer } from '../models/shared/Offer'
import { useFirebaseQuery, useFirebaseQueryOptions } from '../hooks/firebaseHooks'
import { useOrganization } from '../context/OrganizationContext'
import { ActiveTimeRecord } from '../models/shared/TimeRecord'
import { where } from 'firebase/firestore'
import { UserRole } from '../models/shared/UserInvitation'
import { CARD_LAYOUT_FIELD } from '../models/shared/CardLayout'

interface Props {
    preview?: boolean
    layout: CardLayout
    deal: Deal
    onDealSelect?: (deal: Deal) => void
    onDealArchive?: (deal: Deal) => void
    onDealShare?: (deal: Deal) => void
}

export function DealCardWrapper(props: Props) {
    const { deal } = props
    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        isOver,
    } = useSortable({
        id: deal.id || '',
        data: {
            stepId: deal.stepId,
        }
    });

    const showPlaceholder = isOver && !isDragging;

    const style = {
        opacity: isDragging ? 0.2 : 1,
        touchAction: 'none',
    };

    const handleDealClick = (e: React.MouseEvent) => {
        if (!props.onDealSelect) {
            return;
        }
        const target = e.target as HTMLElement;
        const isInDropdown = target.closest('.dropdown');
        if (isInDropdown) {
            return;
        }

        props.onDealSelect(deal);
    }

    return <div className='gap-3 d-flex flex-column' ref={setNodeRef} {...attributes} {...listeners} style={style}
        onClick={handleDealClick}
    >
        {showPlaceholder && <DealCardPlaceholder />}

        <DealCard {...props} />
    </div>
}

export function DealCardPlaceholder() {
    return <Card
        style={{ borderBottom: 'none' }}
    >
        <Card.Header ></Card.Header>
    </Card>
}

export function DealCard(props: Props) {
    const { deal, layout } = props
    const { orgId, organization, orgRole } = useOrganization();
    const isWorker = orgRole === UserRole.WORKER;
    const [offers] = useFirebaseQuery<Offer>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${deal.id}/${COLLECTIONS.OFFERS}`);
    const hoursTrackingEnabled = !!organization.features?.hoursTracking;
    const [activeRecords] = useFirebaseQueryOptions<ActiveTimeRecord>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ACTIVE_TIME_RECORDS}`, {
        queryConstraints: [where('dealId', '==', deal.id || '')],
        skip: !hoursTrackingEnabled || !deal.id,
    });
    const isTracking = activeRecords.length > 0;
    const text = useText();

    const renderContent = (field: CARD_LAYOUT_FIELD) => {
        if (isWorker && field === CARD_LAYOUT_FIELD.OFFER) {
            return '';
        }
        return renderDealContent(field, deal, offers, text);
    }

    const editDeal = async () => {
        props.onDealSelect && props.onDealSelect(deal)
    }

    const archiveDeal = async () => {
        props.onDealArchive && props.onDealArchive(deal)
    }

    const shareDeal = async () => {
        props.onDealShare && props.onDealShare(deal)
    }

    if (isDealSeparator(deal)) {
        return <div className='deal-card px-3'>
            <hr />
        </div>
    }

    return <Card
        className='shadow-sm deal-card'
    >
        <Card.Body className='d-flex flex-column gap-2'>
            <Tags deal={deal} />
            <div className='d-flex justify-content-between align-items-start'>
                <div>
                    {deal.dueAt && <div className='d-flex'><DealDueDate deal={deal} readOnly /></div>}
                    <p className='m-0 d-flex align-items-center gap-2'>
                        {isTracking && <span className='recording-dot' />}
                        {renderContent(layout.title)}
                    </p>
                    <p className='m-0 small text-muted'>{renderContent(layout.subtitle)}</p>
                </div>
                <Dropdown >
                    <Dropdown.Toggle disabled={props.preview} as={ThreeDotsToggle} size='sm' btnClassName='p-0' />
                    <Dropdown.Menu>
                        <AdvancedDropdownItem onClick={editDeal} icon='bi-pencil' text={text.edit} />
                        <AdvancedDropdownItem as={Link} link={PATHS.CARD_EDIT} icon='bi-window' text={text.changeLayout} />
                        <AdvancedDropdownItem onClick={shareDeal} icon='bi-share' text={text.share} />
                        <Dropdown.Divider />
                        <AdvancedDropdownItem onClick={archiveDeal} icon='bi-trash' text={text.archive} />
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <Card.Text className='small text-muted d-flex justify-content-end mt-auto'>
                {renderContent(layout.rightBottom)}
            </Card.Text>
        </Card.Body>
    </Card>
}

